/**
 * IndexedDB-backed QueueStore.
 *
 * Uses a single object store keyed on `id`. No indexes — the queue is
 * scanned linearly during replay, which is fine because the queue size
 * stays small (it drains every time the device comes online).
 *
 * The IDB API is callback-based, so we wrap each operation in a tiny
 * promise adapter. The SW and the main thread both call this code; the
 * `globalThis.indexedDB` reference resolves to the right runtime in
 * either context.
 */

import type { QueueStore, QueuedMutation } from './types'

const DB_NAME    = 'readyroom-sync'
const DB_VERSION = 1
const STORE_NAME = 'mutations'

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (): void => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    req.onsuccess = (): void => resolve(req.result)
    req.onerror   = (): void => reject(req.error ?? new Error('indexedDB.open failed'))
  })
}

function tx<T>(
  mode: IDBTransactionMode,
  run:  (store: IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const trans = db.transaction(STORE_NAME, mode)
    const store = trans.objectStore(STORE_NAME)
    const out   = run(store)
    if (out instanceof Promise) {
      out.then(resolve, reject)
    } else {
      out.onsuccess = (): void => resolve(out.result)
      out.onerror   = (): void => reject(out.error ?? new Error('idb operation failed'))
    }
  }))
}

export function createIdbStore(): QueueStore {
  return {
    async put(m: QueuedMutation): Promise<void> {
      await tx<IDBValidKey>('readwrite', (store) => store.put(m))
    },
    async list(): Promise<readonly QueuedMutation[]> {
      return tx<QueuedMutation[]>('readonly', (store) => store.getAll() as IDBRequest<QueuedMutation[]>)
    },
    async remove(id: string): Promise<void> {
      await tx<undefined>('readwrite', (store) => store.delete(id) as IDBRequest<undefined>)
    },
    async count(): Promise<number> {
      return tx<number>('readonly', (store) => store.count())
    },
    async clear(): Promise<void> {
      await tx<undefined>('readwrite', (store) => store.clear() as IDBRequest<undefined>)
    },
  }
}
